import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Casino as CasinoIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { aviatorAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const AviatorSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    lobby_duration: 8000,
    min_bet_amount: 10.00,
    max_bet_amount: 10000.00,
    max_multiplier: 1000.00,
    max_crash_point: 1000.00,
    house_edge: 0.03,
    instant_crash_probability: 0.01,
    max_auto_cashout: 100.00,
    multiplier_growth_rate: 0.10,
    status: 'active',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await aviatorAPI.getSettings();
      if (response.data.success) {
        const settingsData = response.data.settings;
        setSettings(settingsData);
        setFormData({
          lobby_duration: settingsData.lobby_duration || 8000,
          min_bet_amount: parseFloat(settingsData.min_bet_amount) || 10.00,
          max_bet_amount: parseFloat(settingsData.max_bet_amount) || 10000.00,
          max_multiplier: parseFloat(settingsData.max_multiplier) || 1000.00,
          max_crash_point: parseFloat(settingsData.max_crash_point) || 1000.00,
          house_edge: parseFloat(settingsData.house_edge) || 0.03,
          instant_crash_probability: parseFloat(settingsData.instant_crash_probability) || 0.01,
          max_auto_cashout: parseFloat(settingsData.max_auto_cashout) || 100.00,
          multiplier_growth_rate: parseFloat(settingsData.multiplier_growth_rate) || 0.10,
          status: settingsData.status || 'active',
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.min_bet_amount >= formData.max_bet_amount) {
      toast.error('Minimum bet must be less than maximum bet');
      return;
    }
    
    if (formData.house_edge < 0 || formData.house_edge > 1) {
      toast.error('House edge must be between 0 and 1 (0-100%)');
      return;
    }

    try {
      setSaving(true);
      const response = await aviatorAPI.updateSettings(formData);
      
      if (response.data.success) {
        toast.success('Settings updated successfully');
        fetchSettings();
      } else {
        toast.error(response.data.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      toast.error(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = formData.status === 'active' ? 'maintenance' : 'active';
    
    try {
      setSaving(true);
      const response = await aviatorAPI.toggleGameStatus({ status: newStatus });
      
      if (response.data.success) {
        toast.success(`Game ${newStatus === 'active' ? 'activated' : 'set to maintenance mode'}`);
        setFormData({ ...formData, status: newStatus });
        fetchSettings();
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error(err.response?.data?.message || 'Failed to toggle status');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        lobby_duration: settings.lobby_duration,
        min_bet_amount: parseFloat(settings.min_bet_amount),
        max_bet_amount: parseFloat(settings.max_bet_amount),
        max_multiplier: parseFloat(settings.max_multiplier),
        max_crash_point: parseFloat(settings.max_crash_point),
        house_edge: parseFloat(settings.house_edge),
        instant_crash_probability: parseFloat(settings.instant_crash_probability),
        max_auto_cashout: parseFloat(settings.max_auto_cashout),
        multiplier_growth_rate: parseFloat(settings.multiplier_growth_rate),
        status: settings.status,
      });
      toast.info('Form reset to current saved settings');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 2 }} />
            Aviator Game Settings
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </Button>
          </Box>
        </Box>

        {/* Status Alert */}
        <Alert 
          severity={formData.status === 'active' ? 'success' : 'warning'} 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleToggleStatus}
              disabled={saving}
            >
              {formData.status === 'active' ? 'Set Maintenance' : 'Activate'}
            </Button>
          }
        >
          Game Status: <strong>{formData.status.toUpperCase()}</strong>
          {formData.status === 'maintenance' && ' - Players cannot place new bets'}
        </Alert>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Game Timing */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CasinoIcon sx={{ mr: 1 }} />
                    Game Timing
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Lobby Duration"
                        type="number"
                        value={formData.lobby_duration}
                        onChange={(e) => setFormData({ ...formData, lobby_duration: parseInt(e.target.value) })}
                        required
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                        }}
                        helperText="Time players have to place bets (milliseconds)"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Multiplier Growth Rate"
                        type="number"
                        value={formData.multiplier_growth_rate}
                        onChange={(e) => setFormData({ ...formData, multiplier_growth_rate: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 0.01, max: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">per 100ms</InputAdornment>,
                        }}
                        helperText="How fast the multiplier increases"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Betting Limits */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Betting Limits
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Minimum Bet Amount"
                        type="number"
                        value={formData.min_bet_amount}
                        onChange={(e) => setFormData({ ...formData, min_bet_amount: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 1 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Minimum bet per round"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Bet Amount"
                        type="number"
                        value={formData.max_bet_amount}
                        onChange={(e) => setFormData({ ...formData, max_bet_amount: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 10 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        helperText="Maximum bet per round"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Multiplier"
                        type="number"
                        value={formData.max_multiplier}
                        onChange={(e) => setFormData({ ...formData, max_multiplier: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 2 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">x</InputAdornment>,
                        }}
                        helperText="Maximum possible multiplier"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Crash Point"
                        type="number"
                        value={formData.max_crash_point}
                        onChange={(e) => setFormData({ ...formData, max_crash_point: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 1, min: 2 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">x</InputAdornment>,
                        }}
                        helperText="Maximum crash point limit"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Maximum Auto Cashout"
                        type="number"
                        value={formData.max_auto_cashout}
                        onChange={(e) => setFormData({ ...formData, max_auto_cashout: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.01, min: 1.01 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">x</InputAdornment>,
                        }}
                        helperText="Maximum auto-cashout multiplier"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Game Mechanics */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Game Mechanics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="House Edge"
                        type="number"
                        value={formData.house_edge}
                        onChange={(e) => setFormData({ ...formData, house_edge: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.001, min: 0, max: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">
                            ({(formData.house_edge * 100).toFixed(2)}%)
                          </InputAdornment>,
                        }}
                        helperText="House advantage (0.03 = 3%)"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Instant Crash Probability"
                        type="number"
                        value={formData.instant_crash_probability}
                        onChange={(e) => setFormData({ ...formData, instant_crash_probability: parseFloat(e.target.value) })}
                        required
                        inputProps={{ step: 0.001, min: 0, max: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">
                            ({(formData.instant_crash_probability * 100).toFixed(2)}%)
                          </InputAdornment>,
                        }}
                        helperText="Chance of 1.00x crash (0.01 = 1%)"
                      />
                    </Grid>
                  </Grid>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Note:</strong> Changes to game mechanics will affect the next round. 
                    Current round will complete with existing settings.
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            {/* Save Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                  sx={{
                    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                  }}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Last Updated Info */}
        {settings && (
          <Paper sx={{ p: 2, mt: 3, }}>
            <Typography variant="body2" color="text.secondary">
              Last updated: {new Date(settings.updatedAt).toLocaleString()}
              {settings.updatedBy && ` by User ID: ${settings.updated_by}`}
            </Typography>
          </Paper>
        )}
      </Box>
    </motion.div>
  );
};

export default AviatorSettings;
